import { useRef, useState } from "react";
import { CheckCircle2, CircleDashed, FileText, FileUp, Paperclip, Trash2, Upload } from "lucide-react";
import { Badge, Button, cn } from "@ncp/ui";
import { localize } from "../schema";
import type { FormIssue, I18nText, PropertySchema, UploadedFile } from "../types";
import { registerWidget, type WidgetProps } from "./registry";

const T = {
  upload: { en: "Upload", ar: "رفع" },
  replace: { en: "Replace", ar: "استبدال" },
  remove: { en: "Remove", ar: "إزالة" },
  drop: { en: "Drop a file here or click to browse", ar: "أفلت ملفا هنا أو انقر للاستعراض" },
  uploading: { en: "Uploading…", ar: "جار الرفع…" },
  required: { en: "Required", ar: "مطلوب" },
  optional: { en: "Optional", ar: "اختياري" },
  provided: { en: "Provided", ar: "مرفق" },
  missing: { en: "Missing", ar: "غير مرفق" },
  missingDoc: { en: (n: string) => `${n} is required.`, ar: (n: string) => `${n} مطلوب.` },
  tooLarge: { en: "File is too large.", ar: "الملف كبير جدا." },
  notAccepted: { en: "File type not accepted.", ar: "نوع الملف غير مقبول." },
};

export interface DocumentSpec {
  key: string;
  name: I18nText;
  description?: I18nText;
  required?: boolean;
  accepts?: string[];
  maxSizeMb?: number;
}

export interface DocumentEntry extends UploadedFile {
  documentKey: string;
}

function humanSize(n?: number): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function acceptedBy(file: File, accepts?: string[]): boolean {
  if (!accepts || accepts.length === 0) return true;
  const ext = "." + (file.name.split(".").pop() ?? "").toLowerCase();
  return accepts.some((a) => (a.startsWith(".") ? a.toLowerCase() === ext : a.endsWith("/*") ? file.type.startsWith(a.slice(0, -1)) : file.type === a));
}

/** Uploads through the host context when available; otherwise records the file name only (preview / offline mode). */
async function pushFile(ctx: WidgetProps["context"], file: File, path: string, documentKey?: string): Promise<UploadedFile> {
  if (ctx.uploadFile) return ctx.uploadFile(file, { path, documentKey });
  return { name: file.name, size: file.size, contentType: file.type };
}

function DropZone({ onFiles, accepts, disabled, locale, multiple, compact, label }: { onFiles: (files: File[]) => void; accepts?: string[]; disabled: boolean; locale: "en" | "ar"; multiple?: boolean; compact?: boolean; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) onFiles([...e.dataTransfer.files]);
      }}
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-xl border border-dashed text-sm transition-colors",
        compact ? "px-3 py-2" : "flex-col px-4 py-6",
        over ? "border-aegold-500 bg-aegold-50" : "border-sand-300 bg-white/60 hover:border-aegold-300",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <Upload className={cn("text-aegold-700", compact ? "size-4" : "size-6")} />
      <span className="text-muted-foreground">{label ?? localize(T.drop, locale)}</span>
      {accepts?.length ? <span className="text-[11px] text-sand-600">{accepts.join(", ")}</span> : null}
      <input ref={input} type="file" className="hidden" multiple={multiple} accept={accepts?.join(",")} onChange={(e) => { if (e.target.files) onFiles([...e.target.files]); e.target.value = ""; }} />
    </button>
  );
}

function FileRow({ file, onRemove, locale, canEdit }: { file: UploadedFile; onRemove?: () => void; locale: "en" | "ar"; canEdit: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-sand-200 bg-white/80 px-3 py-2 text-sm">
      <FileText className="size-4 shrink-0 text-aegold-700" />
      <span className="min-w-0 flex-1 truncate" dir="ltr">{file.url ? <a href={file.url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">{file.name}</a> : file.name}</span>
      <span className="text-xs text-sand-600 tabular-nums">{humanSize(file.size)}</span>
      {canEdit && onRemove ? <Button type="button" size="icon-sm" variant="ghost" onClick={onRemove} aria-label={localize(T.remove, locale)}><Trash2 className="text-danger-600" /></Button> : null}
    </div>
  );
}

export function FileWidget(props: WidgetProps) {
  const { value, onChange, disabled, readOnly, locale, options, context, path } = props;
  const file = (value as UploadedFile | undefined) ?? undefined;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const accepts = options.accepts as string[] | undefined;
  const maxMb = Number(options.maxSizeMb ?? 25);
  const canEdit = !disabled && !readOnly;
  const handle = async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    if (!acceptedBy(f, accepts)) return setErr(localize(T.notAccepted, locale));
    if (f.size > maxMb * 1024 * 1024) return setErr(localize(T.tooLarge, locale));
    setErr(null);
    setBusy(true);
    try {
      onChange(await pushFile(context, f, path));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      {file ? <FileRow file={file} locale={locale} canEdit={canEdit} onRemove={async () => { if (context.removeFile) await context.removeFile(file).catch(() => undefined); onChange(undefined); }} /> : null}
      {canEdit && !file ? <DropZone onFiles={handle} accepts={accepts} disabled={busy} locale={locale} label={busy ? localize(T.uploading, locale) : undefined} /> : null}
      {err ? <p className="text-xs text-danger-600">{err}</p> : null}
    </div>
  );
}

export function FileListWidget(props: WidgetProps) {
  const { value, onChange, disabled, readOnly, locale, options, context, path, schema } = props;
  const files = Array.isArray(value) ? (value as UploadedFile[]) : [];
  const [busy, setBusy] = useState(false);
  const accepts = options.accepts as string[] | undefined;
  const canEdit = !disabled && !readOnly;
  const max = schema.maxItems;
  const handle = async (incoming: File[]) => {
    setBusy(true);
    try {
      const added: UploadedFile[] = [];
      for (const f of incoming) if (acceptedBy(f, accepts)) added.push(await pushFile(context, f, path));
      onChange([...files, ...added].slice(0, max ?? undefined));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      {files.map((f, i) => <FileRow key={i} file={f} locale={locale} canEdit={canEdit} onRemove={() => onChange(files.filter((_, j) => j !== i))} />)}
      {canEdit && (!max || files.length < max) ? <DropZone onFiles={handle} accepts={accepts} disabled={busy} locale={locale} multiple compact={files.length > 0} label={busy ? localize(T.uploading, locale) : undefined} /> : null}
    </div>
  );
}

/** Checklist of named documents. Value: `DocumentEntry[]` (one or more files per document key). */
export function DocumentChecklistWidget(props: WidgetProps) {
  const { value, onChange, disabled, readOnly, locale, options, context, path, invalid } = props;
  const docs = (options.documents as DocumentSpec[] | undefined) ?? [];
  const entries = Array.isArray(value) ? (value as DocumentEntry[]) : [];
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const canEdit = !disabled && !readOnly;
  const filesFor = (key: string) => entries.filter((e) => e.documentKey === key);
  const upload = async (doc: DocumentSpec, files: File[]) => {
    const f = files[0];
    if (!f) return;
    if (!acceptedBy(f, doc.accepts)) return setErrors((e) => ({ ...e, [doc.key]: localize(T.notAccepted, locale) }));
    if (f.size > (doc.maxSizeMb ?? 25) * 1024 * 1024) return setErrors((e) => ({ ...e, [doc.key]: localize(T.tooLarge, locale) }));
    setErrors((e) => ({ ...e, [doc.key]: "" }));
    setBusyKey(doc.key);
    try {
      const up = await pushFile(context, f, path, doc.key);
      onChange([...entries, { ...up, documentKey: doc.key }]);
    } finally {
      setBusyKey(null);
    }
  };
  const provided = docs.filter((d) => filesFor(d.key).length > 0).length;
  return (
    <div className={cn("overflow-hidden rounded-xl border bg-white/70", invalid ? "border-danger-300" : "border-sand-200")}>
      <div className="flex items-center justify-between border-b border-sand-100 bg-sand-50/60 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-sand-700">
        <span className="inline-flex items-center gap-1.5"><Paperclip className="size-3.5" /> {localize(T.provided, locale)} {provided}/{docs.length}</span>
      </div>
      <ul className="divide-y divide-sand-100">
        {docs.map((doc) => {
          const files = filesFor(doc.key);
          const ok = files.length > 0;
          return (
            <li key={doc.key} className="grid gap-3 px-4 py-3 md:grid-cols-[1.4fr_1fr]">
              <div className="flex items-start gap-3">
                {ok ? <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success-600" /> : <CircleDashed className={cn("mt-0.5 size-5 shrink-0", doc.required ? "text-warning-600" : "text-sand-400")} />}
                <div className="min-w-0">
                  <p className="text-sm font-medium">{localize(doc.name, locale)} <Badge variant={doc.required ? (ok ? "success" : "warning") : "outline"} size="sm" className="ms-1 align-middle">{doc.required ? localize(T.required, locale) : localize(T.optional, locale)}</Badge></p>
                  {doc.description ? <p className="text-xs text-muted-foreground">{localize(doc.description, locale)}</p> : null}
                  {errors[doc.key] ? <p className="mt-1 text-xs text-danger-600">{errors[doc.key]}</p> : null}
                </div>
              </div>
              <div className="space-y-1.5">
                {files.map((f, i) => <FileRow key={i} file={f} locale={locale} canEdit={canEdit} onRemove={() => onChange(entries.filter((e) => e !== f))} />)}
                {canEdit ? <DropZone compact onFiles={(fs) => upload(doc, fs)} accepts={doc.accepts} disabled={busyKey === doc.key} locale={locale} label={busyKey === doc.key ? localize(T.uploading, locale) : files.length ? `${localize(T.upload, locale)} +` : localize(T.upload, locale)} /> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Missing required documents become validation issues (called by validateSubmission through the widget hook). */
export function validateDocumentChecklist(value: unknown, options: Record<string, unknown>, locale: "en" | "ar", path: string): FormIssue[] {
  const docs = (options.documents as DocumentSpec[] | undefined) ?? [];
  const entries = Array.isArray(value) ? (value as DocumentEntry[]) : [];
  return docs.filter((d) => d.required && !entries.some((e) => e.documentKey === d.key)).map((d) => ({ path, message: T.missingDoc[locale](localize(d.name, locale)), severity: "error" as const, keyword: "document" }));
}

export function documentChecklistSchema(): PropertySchema {
  return {
    type: "array",
    "x-ncp": {
      widget: "documentChecklist",
      options: {
        documents: [
          { key: "tradeLicence", name: { en: "Trade licence", ar: "الرخصة التجارية" }, required: true, accepts: [".pdf", "image/*"] },
          { key: "authorisation", name: { en: "Letter of authorisation", ar: "خطاب التفويض" }, required: true, accepts: [".pdf"] },
          { key: "supporting", name: { en: "Supporting documents", ar: "مستندات داعمة" }, required: false },
        ],
      },
    },
    items: { type: "object", properties: { documentKey: { type: "string" }, id: { type: "string" }, name: { type: "string" }, size: { type: "integer" }, contentType: { type: "string" }, url: { type: "string" } }, required: ["documentKey", "name"] },
  };
}

export function registerDocumentWidgets(): void {
  registerWidget({ key: "file", label: { en: "File upload", ar: "رفع ملف" }, icon: FileUp, category: "documents", forTypes: ["object"], component: FileWidget, defaultSchema: () => ({ type: "object", "x-ncp": { widget: "file", options: { accepts: [".pdf", ".docx", "image/*"], maxSizeMb: 25 } }, properties: { id: { type: "string" }, name: { type: "string" }, size: { type: "integer" }, contentType: { type: "string" }, url: { type: "string" } }, required: ["name"] }), defaultSpan: 12, optionFields: [{ key: "accepts", label: { en: "Accepted types", ar: "الأنواع المقبولة" }, type: "list" }, { key: "maxSizeMb", label: { en: "Max size (MB)", ar: "الحجم الأقصى (ميغابايت)" }, type: "number" }] });
  registerWidget({ key: "fileList", label: { en: "Multiple files", ar: "ملفات متعددة" }, icon: Paperclip, category: "documents", forTypes: ["array"], component: FileListWidget, defaultSchema: () => ({ type: "array", maxItems: 10, "x-ncp": { widget: "fileList", options: { accepts: [".pdf", ".docx", ".xlsx", "image/*"] } }, items: { type: "object", properties: { id: { type: "string" }, name: { type: "string" }, size: { type: "integer" }, contentType: { type: "string" }, url: { type: "string" } }, required: ["name"] } }), defaultSpan: 12 });
  registerWidget({ key: "documentChecklist", label: { en: "Document checklist", ar: "قائمة المستندات" }, description: { en: "Named required/optional documents with per-item upload.", ar: "مستندات مطلوبة/اختيارية مسماة مع رفع لكل عنصر." }, icon: Paperclip, category: "documents", forTypes: ["array"], component: DocumentChecklistWidget, ownLabel: false, defaultSchema: documentChecklistSchema, defaultSpan: 12, validate: validateDocumentChecklist });
}
